/**
 * CFG-VAL-001 Controlled Validation Grammar v1 — frontend helpers.
 * Server remains authoritative; these helpers drive structured Admin authoring only.
 */

export type CfgValValueType =
  | 'STRING'
  | 'TEXT'
  | 'NUMBER'
  | 'DATE'
  | 'DATETIME'
  | 'ENUM'
  | 'REF_CONTRACTOR'
  | 'REF_VEHICLE'
  | 'REF_PERSON';

export const CFG_VAL_INPUT_FORMAT_NONE = 'NONE';
export const CFG_VAL_INPUT_FORMAT_RU_PLATE_TYPE1 = 'RU_PLATE_TYPE1';
export const CFG_VAL_INPUT_FORMAT_RU_PLATE_TYPE1A = 'RU_PLATE_TYPE1A';
export const CFG_VAL_INPUT_FORMAT_RU_PLATE_TYPE1B = 'RU_PLATE_TYPE1B';
export const CFG_VAL_INPUT_FORMAT_RU_PLATE_TYPE2 = 'RU_PLATE_TYPE2';
export const CFG_VAL_INPUT_FORMAT_RU_PLATE_TYPE3 = 'RU_PLATE_TYPE3';
/** Contractor selects concrete plate type at fill time (recommended for new plate fields). */
export const CFG_VAL_INPUT_FORMAT_RU_PLATE_SELECTABLE = 'RU_PLATE_SELECTABLE';

/** Virtual UI family key — never persisted; concrete type lives in validation.input_format. */
export const CFG_VAL_INPUT_FORMAT_RU_PLATE_FAMILY = 'RU_PLATE_FAMILY';

export const CFG_VAL_RU_PLATE_FORMAT_CODES = [
  CFG_VAL_INPUT_FORMAT_RU_PLATE_TYPE1,
  CFG_VAL_INPUT_FORMAT_RU_PLATE_TYPE1A,
  CFG_VAL_INPUT_FORMAT_RU_PLATE_TYPE1B,
  CFG_VAL_INPUT_FORMAT_RU_PLATE_TYPE2,
  CFG_VAL_INPUT_FORMAT_RU_PLATE_TYPE3,
] as const;

const STRING_TEXT = new Set(['STRING', 'TEXT']);
const STRING_TEXT_INPUT_FORMATS = new Set([
  CFG_VAL_INPUT_FORMAT_NONE,
  ...CFG_VAL_RU_PLATE_FORMAT_CODES,
  CFG_VAL_INPUT_FORMAT_RU_PLATE_SELECTABLE,
]);

export function isCfgValRuPlateFormat(code: string | null | undefined): boolean {
  return (
    typeof code === 'string' &&
    (CFG_VAL_RU_PLATE_FORMAT_CODES as readonly string[]).includes(code)
  );
}

export function isCfgValRuPlateSelectable(code: string | null | undefined): boolean {
  return code === CFG_VAL_INPUT_FORMAT_RU_PLATE_SELECTABLE;
}

export function isCfgValRuPlateFamilyStored(code: string | null | undefined): boolean {
  return isCfgValRuPlateFormat(code) || isCfgValRuPlateSelectable(code);
}
const TYPE_ONLY = new Set(['DATE', 'DATETIME', 'REF_CONTRACTOR', 'REF_VEHICLE', 'REF_PERSON']);

export function isGrammarV1Validation(validation: unknown): boolean {
  if (validation === null || typeof validation !== 'object' || Array.isArray(validation)) {
    return false;
  }
  return (validation as { version?: unknown }).version === 1;
}

/** Empty object or missing — treat as editable blank that should become v1 on edit. */
export function isBlankValidation(validation: unknown): boolean {
  if (validation === null || validation === undefined) {
    return true;
  }
  if (typeof validation !== 'object' || Array.isArray(validation)) {
    return false;
  }
  return Object.keys(validation as Record<string, unknown>).length === 0;
}

export function isLegacyPreserveValidation(validation: unknown): boolean {
  if (isBlankValidation(validation)) {
    return false;
  }
  return !isGrammarV1Validation(validation);
}

export function defaultValidationRuleV1(valueType: string): Record<string, unknown> {
  if (valueType === 'ENUM') {
    // Incomplete local draft only — never invent a business allowed value.
    return { version: 1, allowed_values: [] };
  }
  return { version: 1 };
}

export function enumValidationDraftIssues(validation: Record<string, unknown>): string[] {
  const values = readAllowedValues(validation);
  if (values.length === 0 || values.every((item) => item.length === 0)) {
    return ['Добавьте хотя бы одно непустое разрешённое значение'];
  }
  if (values.some((item) => item.length === 0)) {
    return ['Разрешённые значения не должны быть пустыми'];
  }
  if (new Set(values).size !== values.length) {
    return ['Разрешённые значения должны быть уникальными'];
  }
  return [];
}

export function isEnumValidationIncomplete(
  valueType: string,
  validation: Record<string, unknown>,
): boolean {
  if (valueType !== 'ENUM') {
    return false;
  }
  if (isLegacyPreserveValidation(validation)) {
    return false;
  }
  return (
    enumValidationDraftIssues(
      isBlankValidation(validation) ? defaultValidationRuleV1('ENUM') : validation,
    ).length > 0
  );
}

export function validationFamily(
  valueType: string,
): 'string' | 'number' | 'enum' | 'type_only' | 'unknown' {
  if (STRING_TEXT.has(valueType)) {
    return 'string';
  }
  if (valueType === 'NUMBER') {
    return 'number';
  }
  if (valueType === 'ENUM') {
    return 'enum';
  }
  if (TYPE_ONLY.has(valueType)) {
    return 'type_only';
  }
  return 'unknown';
}

/** Serialize only approved v1 keys for the given value_type. */
export function serializeValidationRuleV1(
  valueType: string,
  draft: Record<string, unknown>,
): Record<string, unknown> {
  const family = validationFamily(valueType);
  if (family === 'string') {
    const out: Record<string, unknown> = { version: 1 };
    if (
      typeof draft.min_length === 'number' &&
      Number.isInteger(draft.min_length) &&
      draft.min_length >= 0
    ) {
      out.min_length = draft.min_length;
    }
    if (
      typeof draft.max_length === 'number' &&
      Number.isInteger(draft.max_length) &&
      draft.max_length >= 0
    ) {
      out.max_length = draft.max_length;
    }
    if (
      typeof draft.input_format === 'string' &&
      STRING_TEXT_INPUT_FORMATS.has(draft.input_format) &&
      draft.input_format !== CFG_VAL_INPUT_FORMAT_NONE
    ) {
      out.input_format = draft.input_format;
    }
    return out;
  }
  if (family === 'number') {
    const out: Record<string, unknown> = { version: 1 };
    if (typeof draft.minimum === 'number' && Number.isFinite(draft.minimum)) {
      out.minimum = draft.minimum;
    }
    if (typeof draft.maximum === 'number' && Number.isFinite(draft.maximum)) {
      out.maximum = draft.maximum;
    }
    return out;
  }
  if (family === 'enum') {
    const raw = Array.isArray(draft.allowed_values) ? draft.allowed_values : [];
    const allowed_values = raw
      .filter((item): item is string => typeof item === 'string')
      .map((item) => item);
    return { version: 1, allowed_values };
  }
  return { version: 1 };
}

/**
 * On explicit value_type change: if current is editable v1 (or blank), reset to
 * canonical v1 for the target family. Legacy preserve-only is not silently reinterpreted.
 */
export function validationAfterValueTypeChange(
  previousValidation: Record<string, unknown>,
  nextValueType: string,
): { readonly validation: Record<string, unknown>; readonly blocked: boolean } {
  if (isLegacyPreserveValidation(previousValidation)) {
    return { validation: previousValidation, blocked: true };
  }
  return { validation: defaultValidationRuleV1(nextValueType), blocked: false };
}

export function legacyValidationPreserveNotice(validation: Record<string, unknown>): string {
  const keys = Object.keys(validation);
  if (keys.length === 0) {
    return 'Дополнительное правило сохранено без изменений';
  }
  return `Дополнительное правило сохранено без изменений (${keys.join(', ')})`;
}

export const TYPE_ONLY_VALIDATION_COPY =
  'Дополнительные правила проверки для этого типа в v1 не настраиваются; выполняется проверка типа.';

export function parseOptionalNonNegInt(raw: string): number | undefined {
  const trimmed = raw.trim();
  if (trimmed === '') {
    return undefined;
  }
  const n = Number(trimmed);
  if (!Number.isInteger(n) || n < 0) {
    return undefined;
  }
  return n;
}

export function parseOptionalFiniteNumber(raw: string): number | undefined {
  const trimmed = raw.trim();
  if (trimmed === '') {
    return undefined;
  }
  const n = Number(trimmed);
  if (!Number.isFinite(n)) {
    return undefined;
  }
  return n;
}

export function readAllowedValues(validation: Record<string, unknown>): string[] {
  const raw = validation.allowed_values;
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.filter((item): item is string => typeof item === 'string');
}
